/**
 * App.jsx — root component.
 *
 * Providers (outside-in):
 *   QueryClientProvider → AuthProvider → Router → Routes
 *
 * Global UI:
 *   Navbar, ToastContainer
 */
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { QueryClientProvider } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import { queryClient } from '@/lib/queryClient';
import { AuthProvider } from '@/components/auth/AuthContext';
import AuthCallback from '@/components/auth/AuthCallback';

import { ProtectedRoute } from '@/components/auth/ProtectedRoute';
import { ROLES } from '@/lib/constants';

import Navbar from '@/components/dashboard/Navbar';
import ToastContainer from '@/components/ui/ToastContainer';
import ContributorPortal from '@/components/contributor/ContributorPortal';
import GiveNowModal from '@/components/giving/GiveNowModal';
import CartModal from '@/components/cart/CartModal';

import LoginPage from '@/components/auth/LoginPage';
import MapPage from '@/pages/MapPage';
import ContributorDashboard from '@/pages/ContributorDashboard';
import SiteOwnerDashboard from '@/pages/SiteOwnerDashboard';
import TimelinesPage from '@/pages/TimelinesPage';
import BillingPage from '@/pages/BillingPage';
import CompanyPage from '@/pages/CompanyPage';
import AdminPanel from '@/pages/AdminPanel';

export default function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <AuthProvider>
        <BrowserRouter>
          {/* Full-screen flex column */}
          <div className="flex h-screen flex-col overflow-hidden bg-impact-dark text-slate-100">
            <Navbar />

            <Routes>
              {/* Public */}
              <Route path="/" element={<MapPage />} />
              <Route path="/login" element={<LoginPage />} />
              <Route path="/auth/callback" element={<AuthCallback />} />

              {/* Contributor+ */}
              <Route
                path="/my-catalysts"
                element={
                  <ProtectedRoute requiredRole={ROLES.CONTRIBUTOR}>
                    <div className="flex-1 overflow-y-auto">
                      <ContributorDashboard />
                    </div>
                  </ProtectedRoute>
                }
              />

              {/* Site Owner+ */}
              <Route
                path="/dashboard"
                element={
                  <ProtectedRoute requiredRole={ROLES.SITE_OWNER}>
                    <div className="flex-1 overflow-y-auto">
                      <SiteOwnerDashboard />
                    </div>
                  </ProtectedRoute>
                }
              />

              <Route
                path="/timelines"
                element={
                  <ProtectedRoute requiredRole={ROLES.CONTRIBUTOR}>
                    <div className="flex-1 overflow-y-auto">
                      <TimelinesPage />
                    </div>
                  </ProtectedRoute>
                }
              />

              {/* Billing — any authenticated user with a subscription */}
              <Route
                path="/billing"
                element={
                  <ProtectedRoute requiredRole={ROLES.CONTRIBUTOR}>
                    <div className="flex-1 overflow-y-auto">
                      <BillingPage />
                    </div>
                  </ProtectedRoute>
                }
              />

              {/* Company (licence owners — contributor+) */}
              <Route
                path="/company"
                element={
                  <ProtectedRoute requiredRole={ROLES.CONTRIBUTOR}>
                    <div className="flex-1 overflow-y-auto">
                      <CompanyPage />
                    </div>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/company/:orgId"
                element={
                  <ProtectedRoute requiredRole={ROLES.CONTRIBUTOR}>
                    <div className="flex-1 overflow-y-auto">
                      <CompanyPage />
                    </div>
                  </ProtectedRoute>
                }
              />

              {/* Admin only */}
              <Route
                path="/admin"
                element={
                  <ProtectedRoute requiredRole={ROLES.ADMIN}>
                    <div className="flex-1 overflow-y-auto">
                      <AdminPanel />
                    </div>
                  </ProtectedRoute>
                }
              />

              {/* Stripe redirects after 3DS */}
              <Route path="/give/confirm" element={<Navigate to="/" replace />} />
              <Route path="/subscribe/confirm" element={<Navigate to="/dashboard" replace />} />

              {/* Catch-all */}
              <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
          </div>

          {/* Global modals — mounted here so they work on every route */}
          <ContributorPortal />
          <GiveNowModal />
          <CartModal />
          <ToastContainer />
        </BrowserRouter>
        </AuthProvider>

      {import.meta.env.DEV && <ReactQueryDevtools initialIsOpen={false} />}
    </QueryClientProvider>
  );
}
